import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  toggleCollapse: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // Initialize from localStorage or default to false (expanded)
  const [isCollapsed, setIsCollapsedState] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebar_collapsed');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sidebar_collapsed', String(isCollapsed));
    } catch {
      // Ignore localStorage errors
    }
  }, [isCollapsed]);

  const setIsCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsedState(collapsed);
  }, []);

  const toggleCollapse = useCallback(() => {
    setIsCollapsedState(prev => !prev);
  }, []);

  const value: SidebarContextType = {
    isCollapsed,
    setIsCollapsed,
    toggleCollapse,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

// Optional hook that doesn't throw if used outside provider (returns default values)
export function useSidebarSafe(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    // Return safe defaults for pages that don't need sidebar state
    return {
      isCollapsed: false,
      setIsCollapsed: () => {},
      toggleCollapse: () => {},
    };
  }
  return context;
}

export default SidebarContext;
